import { Base } from '../../abstract/base.model';
import { SafetyTestCategory } from './safety-test-category.model';
import { SafetyTestStatus } from './safety-test-status.model';
import { isRecord } from '../utils/is-record';
import { nullableBoolean, nullableNumber, nullableString } from '../utils/wire-value-utils';

const DEFAULT_PASSING_SCORE = 70;

/**
 * A safety test with the signed-in user's status, built from the hub's safety test JSON
 * (`user-safety-tests-controller.ts`, hub `data-models/safety-test.ts`). The hub's `expiresAt` (when the
 * user's pass expires) is `expirationDate` here.
 *
 * Read-only for web users (tests are authored by admins), so display fields carry defaults when missing,
 * and a `null` the hub sends for "no attempt yet" reads as `undefined`.
 */
export class SafetyTest extends Base {
  categoryId?: string;
  category?: SafetyTestCategory;
  title: string;
  shortName?: string;
  description?: string;
  icon: string;
  status: SafetyTestStatus;
  lastAttemptDate?: string;
  lastScore?: number;
  passingScore: number;
  expirationDate?: string;
  requiredAnnually: boolean;
  questionCount?: number;
  /** In minutes. */
  timeLimit?: number;
  hasStudyGuide: boolean;
  version?: number;
  shuffleQuestions?: boolean;
  shuffleOptions?: boolean;
  hasInProgressAttempt?: boolean;
  inProgressAttemptId?: string;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    const category = isRecord(source['category']) ? new SafetyTestCategory(source['category']) : undefined;
    this.categoryId = nullableString(source['categoryId']) ?? undefined;
    this.category = category;
    this.title = nullableString(source['title']) ?? '';
    this.shortName = nullableString(source['shortName']) ?? undefined;
    this.description = nullableString(source['description']) ?? undefined;
    this.icon = category?.icon ?? nullableString(source['icon']) ?? 'bi-clipboard-check';
    this.status = (nullableString(source['status']) ?? 'not_started') as SafetyTestStatus;
    this.lastAttemptDate = nullableString(source['lastAttemptDate']) ?? undefined;
    this.lastScore = nullableNumber(source['lastScore']) ?? undefined;
    this.passingScore = nullableNumber(source['passingScore']) ?? DEFAULT_PASSING_SCORE;
    this.expirationDate = nullableString(source['expiresAt']) ?? undefined;
    this.requiredAnnually = category?.isRequired ?? nullableBoolean(source['isRequired']) ?? true;
    this.questionCount = nullableNumber(source['questionCount']) ?? undefined;
    this.timeLimit = nullableNumber(source['timeLimit']) ?? undefined;
    this.hasStudyGuide = nullableBoolean(source['hasStudyGuide']) ?? false;
    this.version = nullableNumber(source['version']) ?? undefined;
    this.shuffleQuestions = nullableBoolean(source['shuffleQuestions']) ?? undefined;
    this.shuffleOptions = nullableBoolean(source['shuffleOptions']) ?? undefined;
    this.hasInProgressAttempt = nullableBoolean(source['hasInProgressAttempt']) ?? undefined;
    this.inProgressAttemptId = nullableString(source['inProgressAttemptId']) ?? undefined;
  }
}
